package nl.staartvin.solarpower.data.currentuvindex

import io.ktor.client.HttpClient
import io.ktor.client.call.body
import io.ktor.client.request.get
import io.ktor.client.request.parameter
import kotlinx.datetime.Instant
import nl.staartvin.solarpower.data.model.Location
import nl.staartvin.solarpower.data.model.UvDay
import nl.staartvin.solarpower.data.model.UvPoint
import nl.staartvin.solarpower.data.model.UvSource

/**
 * Global UV-index source. Returns both past (`history`) and future
 * (`forecast`) hourly samples from currentuvindex.com.
 *
 * "Now" semantics: we use the API's `now` field directly — the latest hourly
 * snapshot the upstream has, with its real timestamp. We could interpolate
 * to the device's exact clock but that would mean reading into the forecast
 * for any moment past the latest sample, which is less honest than reporting
 * "latest known reading at HH:MM."
 */
class CurrentUvIndexClient(
    private val http: HttpClient,
    // Overridable so the web build can point at a same-origin proxy path
    // (browser CORS) instead of the upstream directly. Native apps use the
    // default. Full endpoint URL, not just a host.
    private val baseUrl: String = DEFAULT_BASE_URL,
) {

    suspend fun fetch(location: Location): UvDay {
        val dto: UviResponseDto = http
            .get(baseUrl) {
                parameter("latitude", location.latitude)
                parameter("longitude", location.longitude)
            }
            .body()

        val now = UvPoint(Instant.parse(dto.now.time), dto.now.uvi)
        val history = dto.history.orEmpty().map { UvPoint(Instant.parse(it.time), it.uvi) }
        val forecast = dto.forecast.map { UvPoint(Instant.parse(it.time), it.uvi) }

        return UvDay(
            location = location,
            now = now,
            // `now` is included in both lists so the past and future curves
            // visually connect at the same (time, value) point.
            forecast = listOf(now) + forecast,
            measured = history + now,
            source = UvSource.CURRENT_UV_INDEX,
        )
    }

    companion object {
        const val DEFAULT_BASE_URL = "https://currentuvindex.com/api/v1/uvi"
    }
}
